import {
  DataLayerProductSelectItem,
  DataLayerEECProductClick,
  DataLayerProductImpressionList,
  DataLayerViewItemList,
  DataLayerHotelDetailsClick,
  DataLayerClickPromoSortBy,
} from "@/modules/data-layer";
import { calculateDaysBeforeDeparture } from "@/utils/dateUtils";
import type { Product } from "vacv-common/src/types/api/promotion/Api";
import { ProductExtended, PromosOutputParams } from "@/../types/promos-types";
import {
  DataLayerUAEvent,
  DataLayerViewItem,
  DataLayerProductImpression,
  DataLayerHotelDetail,
  DataLayerPromoSortBy,
} from "@/../types/data-layer";

import { CallbackFunction } from "@/../types/common";

window.dataLayer = window.dataLayer || [];

/**
 * Pushes view items & products impressions to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
export function pushDataLayerSearch(
  products: ProductExtended[],
  promosParams: PromosOutputParams,
  accumulatedCount: number
): void {
  pushDataLayerViewItems(products, promosParams, accumulatedCount);
  window.dataLayer.push({
    ecommerce: null,
  });
  pushDataLayerProductImpressions(products, promosParams, accumulatedCount);
  window.dataLayer.push({
    ecommerce: null,
  });
}

/**
 * Pushes selecte item & product click to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
export function pushDataLayerClickPromo(
  promotion: Product,
  promosParams: PromosOutputParams,
  callback: CallbackFunction
): void {
  window.dataLayer.push(
    DataLayerProductSelectItem.create(
      createSelectItems(promotion, promosParams)
    )
  );
  window.dataLayer.push({
    ecommerce: null,
  });
  window.dataLayer.push(
    DataLayerEECProductClick.create(
      createProducts(promotion, promosParams),
      callback
    )
  );
  window.dataLayer.push({
    ecommerce: null,
  });
  callback();
}

/**
 * Pushes Hotel details to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
export const pushDataLayerClickHotelDetails = (
  promotion: Product,
  elementName: string
) => {
  window.dataLayer.push(
    DataLayerHotelDetailsClick.create(
      createHotelDetails(promotion, elementName)
    )
  );
};

/**
 * Pushes promo sort rule to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
export const pushDataLayerClickPromoSortBy = (sortBy: string) => {
  window.dataLayer.push(
    DataLayerClickPromoSortBy.create(createPromoSortBy(sortBy))
  );
};

/**
 * Pushes search result products impressions to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
function pushDataLayerProductImpressions(
  products: ProductExtended[],
  promosParams: PromosOutputParams,
  accumulatedCount: number
): void {
  window.dataLayer.push(
    DataLayerProductImpressionList.create(
      createProductImpressions(products, promosParams, accumulatedCount)
    )
  );
}

/**
 * Pushes search result view items to the DataLayer[] in the required format.
 *
 * @param {JSON} result A PLP search result
 */
function pushDataLayerViewItems(
  products: ProductExtended[],
  promosParams: PromosOutputParams,
  accumulatedCount: number
): void {
  window.dataLayer.push(
    DataLayerViewItemList.create(
      createViewItems(products, promosParams, accumulatedCount)
    )
  );
}

function createProducts(
  promotion: Product,
  promosParams: PromosOutputParams
): Array<DataLayerUAEvent> {
  const products: Array<DataLayerUAEvent> = [];
  products.push(createProduct(promotion, 0, promosParams));

  return products;
}

function createProduct(
  promotion: Product,
  index: number,
  promosParams: PromosOutputParams
): DataLayerProductImpression {
  const dbd = calculateDaysBeforeDeparture(promotion.departure_date);
  return {
    name: promotion.hotel_name ?? "",
    id: createId(promosParams, promotion, dbd),
    price: promotion.price?.toFixed(2),
    brand: "",
    category: "Package",
    variant: "Economy",
    position: index + 1,
  };
}

function createId(
  promosParams: PromosOutputParams,
  promotion: Product,
  dbd: number
) {
  return `Package | ${promosParams.gateway} | ${promosParams.destination} | ${promotion.hotel_name} | ${promosParams.year_month} | ${promosParams.duration} Nights | ${dbd} DBD`;
}

function createSelectItems(
  promotion: Product,
  promosParams: PromosOutputParams
): Array<DataLayerViewItem> {
  const selectItems: Array<DataLayerViewItem> = [];
  selectItems.push(createItem(promotion, promosParams, 0));

  return selectItems;
}

function createItem(
  promotion: Product,
  promosParams: PromosOutputParams,
  index: number
): DataLayerViewItem {
  const dbd = calculateDaysBeforeDeparture(promotion.departure_date);
  return {
    item_name: promotion.hotel_name ?? "",
    item_id: createId(promosParams, promotion, dbd),
    item_brand: "",
    item_category: "Vacation Package",
    item_category2: promosParams.gateway,
    item_category3: promosParams.destination,
    item_category4: "",
    item_variant: "Economy",
    item_list_name: "Package Promo Page",
    item_list_id: "",
    index: index + 1,
    quantity: 1,
    price: promotion.price?.toFixed(2),
    psychologic_trigger: "none",
  };
}

function createViewItems(
  products: ProductExtended[],
  promosParams: PromosOutputParams,
  accumulatedCount: number
): Array<DataLayerViewItem> {
  const viewItems: Array<DataLayerViewItem> = [];
  products?.forEach((product: Product, index: number) => {
    viewItems.push(createItem(product, promosParams, index + accumulatedCount));
  });
  return viewItems;
}

function createProductImpressions(
  products: ProductExtended[],
  promosParams: PromosOutputParams,
  accumulatedCount: number
): Array<DataLayerProductImpression> {
  return products?.map((product: Product, index: number) =>
    createProduct(product, index + accumulatedCount, promosParams)
  );
}

function createHotelDetails(
  details: Product,
  elementName: string
): DataLayerHotelDetail {
  return {
    element_name: elementName,
    hotel_name: details.hotel_name ?? "",
  };
}

function createPromoSortBy(sortBy: string): DataLayerPromoSortBy {
  return {
    sortBy: sortBy,
  };
}
